<template>
	<div class="layer-manager-panel">
		<div class="panel-header">
			<div class="header-title">
				<span class="icon">🛰️</span>
				<h3>Livelli Satellite</h3>
			</div>
			<button class="close-btn" @click="$emit('close')">❌</button>
		</div>

		<!-- Search -->
		<div class="search-box">
			<input
				v-model="searchQuery"
				type="text"
				placeholder="Cerca nuovo livello..."
				class="search-input"
			/>
		</div>

		<!-- Active Layers Quick List -->
		<div class="active-summary" v-if="localActiveLayers.length > 0">
			<div class="summary-header">
				<div class="summary-title">Livelli Attivi</div>
				<div class="date-control">
					<button
						class="calendar-btn"
						@click="showCalendar = !showCalendar"
						title="Seleziona data"
					>
						<span class="calendar-icon">📅</span>
						<span class="date-label">{{ formattedDate }}</span>
					</button>

					<!-- [NEW] Clear Date Button -->
					<button
						v-if="props.wmsDate"
						class="clear-date-btn"
						@click="clearDate"
						title="Torna a Oggi"
					>
						✕
					</button>

					<div v-if="showCalendar" class="calendar-popover" @click.stop>
						<MaterialCalendar
							:modelValue="wmsDate"
							:activityDates="mergedAvailableDates"
							@update:modelValue="onDateSelect"
							@close="showCalendar = false"
							@month-change="onCalendarMonthChange"
						/>
					</div>
				</div>
			</div>
			<div class="summary-list">
				<div
					v-for="(layer, index) in localActiveLayers"
					:key="layer.id"
					class="summary-item"
					@click="scrollToLayer(layer.id)"
					style="cursor: pointer"
				>
					<span class="summary-name">{{ layer.title }}</span>
					<button
						class="remove-btn-small"
						@click.stop="removeLayer(index)"
						title="Rimuovi livello"
					>
						✕
					</button>
				</div>
			</div>
		</div>

		<!-- Scrollable List (Available) -->
		<div class="layer-list" v-if="filteredLayers.length > 0">
			<div
				v-for="layer in filteredLayers"
				:key="layer.Name"
				:id="`layer-item-${layer.Name}`"
				class="layer-item"
				:class="{ active: isSelected(layer.Name) }"
				@click.stop="toggleLayer(layer)"
			>
				<div class="item-header">
					<div class="checkbox">
						<span v-if="isSelected(layer.Name)">✓</span>
					</div>
					<div class="layer-info">
						<span class="layer-name">{{ layer.Title }}</span>
						<span class="layer-id">{{ layer.Name }}</span>
					</div>
				</div>

				<!-- Inline Opacity Slider -->
				<div class="inline-opacity" v-if="isSelected(layer.Name)" @click.stop>
					<div class="slider-row">
						<label
							>Opacità
							{{ Math.round(getActiveOpacity(layer.Name) * 100) }}%</label
						>
					</div>
					<input
						type="range"
						min="0"
						max="1"
						step="0.05"
						:value="getActiveOpacity(layer.Name)"
						@input="(e) => updateLayerOpacity(layer.Name, e)"
					/>
				</div>
			</div>
		</div>
		<div v-else class="empty-state">
			<span v-if="loading">Caricamento livelli...</span>
			<span v-else>Nessun livello trovato.</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import MaterialCalendar from "../ui/MaterialCalendar.vue";

interface WMSLayer {
	Name: string;
	Title: string;
	Abstract?: string;
}

interface ActiveLayer {
	id: string;
	title: string;
	opacity: number;
}

const props = defineProps<{
	layers: WMSLayer[];
	activeLayers: ActiveLayer[];
	loading?: boolean;
	wmsDate?: string;
	availableDatesMap?: Record<string, string[]>;
}>();

const emit = defineEmits([
	"update:activeLayers",
	"close",
	"update:wmsDate",
	"check-availability",
]);

const searchQuery = ref("");
const localActiveLayers = ref<ActiveLayer[]>([]);
const showCalendar = ref(false);

// Formatting date for display
const formattedDate = computed(() => {
	if (!props.wmsDate) return "Oggi";
	const d = new Date(props.wmsDate);
	// Check if valid
	if (isNaN(d.getTime())) return props.wmsDate;
	return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
});

// Close calendar on click outside
function handleClickOutside(e: MouseEvent) {
	const target = e.target as HTMLElement;
	if (showCalendar.value && !target.closest(".date-control")) {
		showCalendar.value = false;
	}
}

onMounted(() => {
	document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
	document.removeEventListener("click", handleClickOutside);
});

// Sync prop to local
watch(
	() => props.activeLayers,
	(newVal) => {
		localActiveLayers.value = [...newVal];
	},
	{ immediate: true, deep: true },
);

const filteredLayers = computed(() => {
	if (!searchQuery.value) return props.layers;
	const q = searchQuery.value.toLowerCase();
	return props.layers.filter(
		(l) =>
			l.Title.toLowerCase().includes(q) || l.Name.toLowerCase().includes(q),
	);
});

// [NEW] Merge dates from all active layers
const mergedAvailableDates = computed(() => {
	if (localActiveLayers.value.length === 0) return [];

	// If we have dates for specific layers, merge them.
	// Ideally user selects ONE date for all layers, so we show INTERSECTION or UNION.
	// Union is safer (shows where *something* is available).

	const uniqueDates = new Set<string>();

	localActiveLayers.value.forEach((layer) => {
		const dates = props.availableDatesMap?.[layer.id] || [];
		dates.forEach((d) => uniqueDates.add(d));
	});

	// Also include currently selected date if valid
	if (props.wmsDate) uniqueDates.add(props.wmsDate);

	return Array.from(uniqueDates);
});

function onCalendarMonthChange(event: { year: number; month: number }) {
	// Fetch availability for all active layers for this month
	localActiveLayers.value.forEach((layer) => {
		emit("check-availability", {
			year: event.year,
			month: event.month,
			layerId: layer.id,
		});
	});
}

function isSelected(name: string) {
	return localActiveLayers.value.some((l) => l.id === name);
}

function getActiveOpacity(name: string) {
	const layer = localActiveLayers.value.find((l) => l.id === name);
	return layer ? layer.opacity : 1.0;
}

function toggleLayer(layer: WMSLayer) {
	const idx = localActiveLayers.value.findIndex((l) => l.id === layer.Name);
	if (idx >= 0) {
		// Remove
		localActiveLayers.value.splice(idx, 1);
	} else {
		// Add
		localActiveLayers.value.push({
			id: layer.Name,
			title: layer.Title,
			opacity: 1.0,
		});
	}
	updateActive();
}

function removeLayer(index: number) {
	localActiveLayers.value.splice(index, 1);
	updateActive();
}

function updateLayerOpacity(id: string, event: Event) {
	const val = parseFloat((event.target as HTMLInputElement).value);
	const layer = localActiveLayers.value.find((l) => l.id === id);
	if (layer) {
		layer.opacity = val;
		updateActive();
	}
}

function updateActive() {
	emit("update:activeLayers", localActiveLayers.value);
}

function scrollToLayer(layerId: string) {
	const el = document.getElementById(`layer-item-${layerId}`);
	if (el) {
		el.scrollIntoView({ behavior: "smooth", block: "center" });
	}
}

function onDateSelect(newDate: string) {
	emit("update:wmsDate", newDate);
	showCalendar.value = false;
}

// [NEW] Clear date
function clearDate() {
	emit("update:wmsDate", null);
}
</script>

<style scoped>
.layer-manager-panel {
	position: absolute;
	top: 70px; /* Below top controls */
	left: 20px;
	width: 340px;
	max-height: calc(100% - 100px);
	background: rgba(20, 24, 28, 0.95);
	backdrop-filter: blur(10px);
	border: 1px solid #333;
	border-radius: 12px;
	display: flex;
	flex-direction: column;
	z-index: 3000;
	color: #eee;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
	overflow: hidden;
	/* Font improvement */
	font-family: "Inter", sans-serif;
}

.panel-header {
	padding: 12px 16px;
	border-bottom: 1px solid #333;
	display: flex;
	justify-content: space-between;
	align-items: center;
	background: rgba(255, 255, 255, 0.05);
}

.header-title {
	display: flex;
	align-items: center;
	gap: 8px;
}
.header-title h3 {
	margin: 0;
	font-size: 1rem;
	font-weight: 600;
	letter-spacing: 0.5px;
}

.close-btn {
	background: none;
	border: none;
	cursor: pointer;
	font-size: 1rem;
	padding: 4px;
	opacity: 0.7;
	transition: 0.2s;
	color: white;
}
.close-btn:hover {
	opacity: 1;
}

/* ACTIVE SUMMARY */
.active-summary {
	background: rgba(33, 150, 243, 0.05);
	border-bottom: 1px solid #333;
	padding: 12px 16px;
	flex-shrink: 0;
}

.summary-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
}

.summary-title {
	font-size: 0.75rem;
	font-weight: 700;
	text-transform: uppercase;
	color: #2196f3;
	margin: 0;
	letter-spacing: 1px;
}

/* DATE CONTROL */
.date-control {
	position: relative;
	display: flex;
	align-items: center;
	gap: 4px; /* Added gap for X button */
}

.calendar-btn {
	background: rgba(0, 0, 0, 0.3);
	border: 1px solid #444;
	color: #fff;
	border-radius: 6px;
	padding: 4px 10px;
	font-size: 0.8rem;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 6px;
	transition: all 0.2s;
}

.calendar-btn:hover {
	background: rgba(33, 150, 243, 0.1);
	border-color: #2196f3;
}

/* [NEW] Style for Clear Date Button */
.clear-date-btn {
	background: rgba(0, 0, 0, 0.2);
	border: 1px solid transparent;
	color: #999;
	border-radius: 6px;
	padding: 4px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s;
}
.clear-date-btn:hover {
	background: rgba(231, 76, 60, 0.2);
	color: #e74c3c;
}

.date-label {
	font-weight: 500;
}

.calendar-popover {
	position: absolute;
	top: 36px;
	right: 0;
	z-index: 3000;
}

/* LISTS */
.summary-list {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.summary-item {
	display: flex;
	align-items: center;
	background: rgba(33, 150, 243, 0.2);
	border: 1px solid rgba(33, 150, 243, 0.3);
	border-radius: 20px; /* Pill shape */
	padding: 4px 10px;
	gap: 8px;
	font-size: 0.8rem;
	color: white;
	transition: background 0.2s;
}
.summary-item:hover {
	background: rgba(33, 150, 243, 0.3);
}

.summary-name {
	max-width: 140px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.remove-btn-small {
	background: rgba(0, 0, 0, 0.2);
	border: none;
	color: #ff8a80;
	font-size: 0.7rem;
	cursor: pointer;
	font-weight: bold;
	padding: 0;
	width: 16px;
	height: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	transition: all 0.2s;
}
.remove-btn-small:hover {
	background: #ff5252;
	color: white;
}

/* SEARCH */
.search-box {
	padding: 12px 16px;
	border-bottom: 1px solid #333;
}

.search-input {
	width: 100%;
	background: rgba(255, 255, 255, 0.05);
	border: 1px solid #333;
	border-radius: 8px;
	padding: 10px 12px;
	color: white;
	font-size: 0.9rem;
	transition: border-color 0.2s;
}
.search-input:focus {
	outline: none;
	border-color: #2196f3;
	background: rgba(0, 0, 0, 0.2);
}

.layer-list {
	flex: 1;
	overflow-y: auto;
	padding: 0;
}

.layer-item {
	display: flex;
	flex-direction: column;
	padding: 12px 16px;
	cursor: pointer;
	transition: 0.2s;
	border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.item-header {
	display: flex;
	align-items: center;
	gap: 12px;
}

.layer-item:hover {
	background: rgba(255, 255, 255, 0.05);
}

.layer-item.active {
	background: rgba(33, 150, 243, 0.1);
	border-left: 3px solid #2196f3;
}

.checkbox {
	width: 20px;
	height: 20px;
	border: 2px solid #555;
	border-radius: 6px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.8rem;
	color: white;
	background: rgba(0, 0, 0, 0.2);
	flex-shrink: 0;
	transition: 0.2s;
}

.active .checkbox {
	border-color: #2196f3;
	background: #2196f3;
}

.layer-info {
	display: flex;
	flex-direction: column;
}

.layer-name {
	font-size: 0.9rem;
	font-weight: 500;
	color: #eee;
}
.layer-id {
	font-size: 0.75rem;
	color: #888;
	margin-top: 2px;
}

/* INLINE OPACITY */
.inline-opacity {
	margin-top: 12px;
	padding-top: 8px;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
	padding-left: 34px;
}

.slider-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 6px;
}

.inline-opacity label {
	font-size: 0.75rem;
	color: #ccc;
	font-weight: 500;
}

/* Custom Range Input Styling */
input[type="range"] {
	-webkit-appearance: none;
	appearance: none;
	width: 100%;
	background: transparent;
	cursor: pointer;
	margin: 6px 0;
}

/* Track */
input[type="range"]::-webkit-slider-runnable-track {
	width: 100%;
	height: 4px;
	cursor: pointer;
	background: #444;
	border-radius: 2px;
}
input[type="range"]::-moz-range-track {
	width: 100%;
	height: 4px;
	cursor: pointer;
	background: #444;
	border-radius: 2px;
}

/* Thumb */
input[type="range"]::-webkit-slider-thumb {
	-webkit-appearance: none;
	appearance: none;
	height: 14px;
	width: 14px;
	border-radius: 50%;
	background: #2196f3;
	cursor: pointer;
	margin-top: -5px;
	box-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
	transition: transform 0.1s;
}
input[type="range"]::-webkit-slider-thumb:hover {
	transform: scale(1.2);
}

.empty-state {
	padding: 30px;
	text-align: center;
	color: #777;
	font-size: 0.9rem;
}
</style>
